import { ApiService, ApiSyncType } from "@incendium/api";
import { Box, Grid } from "@mui/material";
import Google_Ads_logo from "Assets/Images/ThirdParty/Google_Ads_logo.png";
import Bing_Ads_logo from "Assets/Images/ThirdParty/Bing_Ads_2016_logo.svg";
import Meta_Ads_logo from "Assets/Images/ThirdParty/Meta_lockup_positive primary_RGB.svg";
import Ecwid_logo from "Assets/Images/ThirdParty/Ecwid_LSLockup_BlackRGB_Ecwid_EN.svg";
import Shopify_logo from "Assets/Images/ThirdParty/shopify_logo_black.png";
import Woo_logo from "Assets/Images/ThirdParty/woocommerce-logo-color-black@2x.png";
import { useSyncClients } from "Hooks/useSyncClients";
import { motion } from "motion/react";
import {
  BingAds,
  GoogleAds,
  SyncCard,
  MetaAds,
  Ecwid,
  DatalayerSycCard,
} from "features/dataConnectors";
import Loading from "Components/Loading/Loading";
import Shopify from "features/dataConnectors/components/Shopify";
import ShopifyConnectButton from "features/dataConnectors/components/ShopifyConnectButton";
import WooCommerce from "features/dataConnectors/components/WooCommerce";
import WooCommerceConnectButton from "features/dataConnectors/components/WooCommerceConnectButton";
import WordpressSyncCard from "features/dataConnectors/components/WordpressSyncCard";
import { OAUTH_REDIRECT_URL } from "features/auth/constants";

const listVariants = {
  hidden: {
    opacity: 0,
    transition: {
      when: "afterChildren",
    },
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      when: "beforeChildren",
    },
  },
};

const itemVariants = {
  hidden: { scale: 0.7, opacity: 0 },
  visible: { scale: 1, opacity: 1 },
};

interface IDataConnectorsListProps {
  type: "platform" | "app";
}
function DataConnectorsList({ type }: IDataConnectorsListProps) {
  const { loading } = useSyncClients();

  if (loading) {
    return (
      <Box display={"flex"} my={4}>
        <Loading />
      </Box>
    );
  }

  return (
    <>
      <Grid
        container
        spacing={3}
        component={motion.div}
        variants={listVariants}
        initial="hidden"
        animate="visible"
      >
        {type === "platform" && (
          <>
            <Grid item xs={4} component={motion.div} variants={itemVariants}>
              <SyncCard
                logo={Google_Ads_logo}
                type={ApiSyncType.GOOGLE_ADS}
                title="Google Ads"
                description="Connect with Google Ads to track user visits from campaigns, access cost data for ROAS calculations, and gain deeper insights."
                service={ApiService.GOOGLE}
                configComponent={GoogleAds}
                drawerMaxWidth={900}
              />
            </Grid>
            <Grid item xs={4} component={motion.div} variants={itemVariants}>
              <SyncCard
                logo={Bing_Ads_logo}
                type={ApiSyncType.BING_ADS}
                title="Bing Ads"
                description="Link with Bing Ads to track user visits from campaigns, retrieve cost data for accurate ROAS calculations, and unlock valuable insights for optimization."
                service={ApiService.BING}
                configComponent={BingAds}
                imageSx={{ maxHeight: 30 }}
                drawerMaxWidth={900}
              />
            </Grid>
            <Grid item xs={4} component={motion.div} variants={itemVariants}>
              <SyncCard
                logo={Meta_Ads_logo}
                type={ApiSyncType.FACEBOOK_ADS}
                title="Meta / Facebook Ads"
                description="Link with Meta Ads to track user visits from campaigns on Facebook, Instagram, and Messenger; retrieve cost data for accurate ROAS calculations, and unlock valuable insights for optimization."
                service={ApiService.FACEBOOK}
                configComponent={MetaAds}
                imageSx={{ transform: `translateX(-12px)` }}
                drawerMaxWidth={1200}
              />
            </Grid>
            <Grid item xs={4} component={motion.div} variants={itemVariants}>
              <DatalayerSycCard />
            </Grid>
          </>
        )}
        {type === "app" && (
          <>
            <Grid item xs={4} component={motion.div} variants={itemVariants}>
              <SyncCard
                logo={Shopify_logo}
                type={ApiSyncType.SHOPIFY_API}
                title="Shopify App"
                description="Connect with your Shopify store for more accurate tracking and ecommerce insights."
                service={ApiService.SHOPIFY}
                configComponent={Shopify}
                state={OAUTH_REDIRECT_URL}
                imageSx={{ maxHeight: 32 }}
                ConnectButton={ShopifyConnectButton}
                linkOnConnect
              />
            </Grid>
            <Grid item xs={4} component={motion.div} variants={itemVariants}>
              <SyncCard
                logo={Woo_logo}
                type={ApiSyncType.WOOCOMMERCE_CONNECTOR}
                title="WooCommerce"
                description="Connect your WooCommerce store for accurate tracking and ecommerce insights."
                service={ApiService.WOOCOMMERCE}
                configComponent={WooCommerce}
                state={OAUTH_REDIRECT_URL}
                imageSx={{ maxHeight: 32 }}
                ConnectButton={WooCommerceConnectButton}
                linkOnConnect
              />
            </Grid>
            <Grid item xs={4} component={motion.div} variants={itemVariants}>
              <SyncCard
                logo={Ecwid_logo}
                type={ApiSyncType.ECWID_API}
                title="Ecwid"
                description="Connect with your ECWID store for more accurate tracking and ecommerce insights."
                service={ApiService.ECWID}
                configComponent={Ecwid}
                state={OAUTH_REDIRECT_URL}
                imageSx={{ maxHeight: 32 }}
              />
            </Grid>
            <Grid item xs={4} component={motion.div} variants={itemVariants}>
              <WordpressSyncCard />
            </Grid>
          </>
        )}
      </Grid>
    </>
  );
}

export default DataConnectorsList;
